import type { ArticleForm, ModuleForm } from '@/generated-api'
import type { ITab } from '@/interfaces/ITab'
import { useLocalizationStore, useDrawerStore } from '@/stores'
import { useAppStore } from '@/stores/portal-stores/general-stores/app'

export const debounce = <F extends (...args: any[]) => void>(func: F, delay: number) => {
  let timeoutId: ReturnType<typeof setTimeout>

  return function (this: ThisParameterType<F>, ...args: Parameters<F>) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      func.apply(this, args)
    }, delay)
  }
}

export const scrollIntoView = (id: string, behaviorValue?: string) => {
  const mainDiv = document.getElementById('main-div')
  const bodyDiv = document.getElementById('body-div')
  const panelElement = document.getElementById(id)

  if (panelElement && mainDiv && bodyDiv) {
    const mainDivRect = mainDiv.getBoundingClientRect()
    const panelRect = panelElement.getBoundingClientRect()
    const offset = panelRect.top - mainDivRect.top - 22

    const behaviorToUse: ScrollBehavior =
      behaviorValue == undefined ? ('smooth' as ScrollBehavior) : (behaviorValue as ScrollBehavior)

    bodyDiv.scrollTo({ top: offset, behavior: behaviorToUse as ScrollBehavior })
  }
}

export const scrollInOfDiv = (id: string, amount: number = 0) => {
  const divToScrollIn = document.getElementById(id)
  divToScrollIn?.scrollTo({ top: amount, behavior: 'smooth' })
}

//Set scrollHitsElement in store when we hit specific element on page
export const scrollHitsElement = (id: string) => {
  const appStore = useAppStore()
  const bodyDiv = document.getElementById('body-div')
  const element = document.getElementById(id)

  const initTime = new Date().getTime()
  let elementHitFirstTime = false

  if (!bodyDiv || !element) {
    console.log('Body div or element not found')
    return
  }

  // Create named function so we can remove it later
  const scrollHandler = () => {
    const clientHeight = bodyDiv!.clientHeight
    const bottomOfElement = element!.getBoundingClientRect().bottom
    const now = new Date().getTime()

    if (
      bottomOfElement < clientHeight &&
      elementHitFirstTime === false &&
      now - initTime > 120000
    ) {
      elementHitFirstTime = true
      appStore.setScrollHitsElement(true)
    }
  }

  bodyDiv.addEventListener('scroll', scrollHandler)

  // Return cleanup function
  return () => {
    bodyDiv.removeEventListener('scroll', scrollHandler)
  }
}

export const setScrollToItem = (id: string, type: string) => {
  localStorage.setItem('itemToScrollTo', JSON.stringify({ id: id, type: type }))
}

const debounceinto = debounce(scrollIntoView, 1)
export const scrollToItem = (type: string) => {
  const storageItem = localStorage.getItem('itemToScrollTo')
  if (storageItem != undefined) {
    const itemToScrollTo = JSON.parse(storageItem)

    if (itemToScrollTo != undefined && itemToScrollTo.type === type) {
      debounceinto(itemToScrollTo.id, 'auto')
    }
  }

  resetScrollToItem()
}

export const resetScrollToItem = () => {
  localStorage.removeItem('itemToScrollTo')
}

//Used on profile inside drawer to scroll to specific section
export const scrollToDrawerSection = (section: string): void => {
  const drawerStore = useDrawerStore()
  if (drawerStore.subDrawer === undefined && drawerStore.shouldScrollToSection === true) {
    const elementPosition = document.getElementById(section ?? '') ?? undefined
    if (elementPosition !== undefined) {
      elementPosition?.scrollIntoView({ behavior: 'instant', block: 'center' })
    }
  }
  drawerStore.shouldScrollToSection = false
}

// Handle click event, used for anchor scroll
export const handleClick = (event: PointerEvent) => {
  const target = event.target as HTMLAnchorElement
  if (target !== undefined && target.hash !== undefined && target.hash !== '') {
    event.preventDefault()
    scrollIntoView(target.hash.replace('#', ''))
  }
}

export const boldSearchTerm = (str: string, query: string): string => {
  // Handle case-insensitive searching and empty query cases
  const normalizedStr = str.toUpperCase()
  const normalizedQuery = query.toUpperCase()

  if (!normalizedQuery) {
    return str // Return original string if query is empty
  }

  const index = normalizedStr.indexOf(normalizedQuery)

  if (index === -1) {
    return str // Return original string if query not found
  }

  const queryLength = normalizedQuery.length

  // Construct the bolded string with proper casing preservation
  let boldedStr = ''
  boldedStr = str.slice(0, index) // Maintain original case

  const beforeMatch = str.slice(index, index + queryLength) // Extract query portion
  const afterMatch = str.slice(index + queryLength) // Maintain original case

  boldedStr += `<strong>${beforeMatch}</strong>` // Bold the query, preserving case
  boldedStr += afterMatch

  return boldedStr // Return the bolded string without wrapping in a `<p>` tag
}

export const replaceUserProfileLink = (text: string | undefined): string => {
  const profileLink = useAppStore().siteContent.settings?.candidateProfileUrl?.toString()

  if (text != undefined && profileLink != undefined) {
    return text.replace('/{{profileLink}}', profileLink)
  }

  return text ?? ''
}

export const isArticleForm = (item: ArticleForm | ModuleForm): item is ArticleForm => {
  return (item as ArticleForm).articleContent !== undefined
}

export const getLocalStorageTab = (title: string, tabs: ITab[]): ITab => {
  const localTabItem = localStorage.getItem('tabItem')

  let tabItem = tabs[0]

  if (localTabItem != undefined) {
    const jsonTabItem = JSON.parse(localTabItem)
    if (title === jsonTabItem?.pageTitle) {
      tabItem = tabs[Number(jsonTabItem.tabId) - 1]
    }
  }

  return tabItem
}

export const setLocalStorageTab = (tabId: number, pageTitle: string) => {
  localStorage.setItem('tabItem', JSON.stringify({ tabId: tabId, pageTitle: pageTitle }))
}

export const generateMonths = () => {
  const ls = useLocalizationStore()
  return [
    {
      id: '01',
      name: ls.translate('general_Month_January')
    },
    {
      id: '02',
      name: ls.translate('general_Month_February')
    },
    {
      id: '03',
      name: ls.translate('general_Month_March')
    },
    {
      id: '04',
      name: ls.translate('general_Month_April')
    },
    {
      id: '05',
      name: ls.translate('general_Month_May')
    },
    {
      id: '06',
      name: ls.translate('general_Month_June')
    },
    {
      id: '07',
      name: ls.translate('general_Month_July')
    },
    {
      id: '08',
      name: ls.translate('general_Month_August')
    },
    {
      id: '09',
      name: ls.translate('general_Month_September')
    },
    {
      id: '10',
      name: ls.translate('general_Month_October')
    },
    {
      id: '11',
      name: ls.translate('general_Month_November')
    },
    {
      id: '12',
      name: ls.translate('general_Month_December')
    }
  ]
}
